<template>
  <div class="checks-start-view">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div class="flex justify-between items-center mb-6 bg-white rounded-xl p-5">
        <div>
          <h1 class="text-xl font-bold text-gray-800 mb-1">Neuen Check starten</h1>
          <p v-if="!showLicensePlateInput" class="text-sm text-gray-600">Scanne das Kennzeichen oder gib es manuell ein</p>
          <p v-else-if="showLicensePlateInput && !searchCompleted" class="text-sm text-gray-600">Bitte gib ein Kennzeichen ein</p>
          <p v-else-if="showLicensePlateInput && searchCompleted && !vehicleFound" class="text-sm text-gray-600">Fahrzeug nicht gefunden. Bitte vervollständige die Daten manuell.</p>
          <p v-else-if="showLicensePlateInput && searchCompleted && vehicleFound" class="text-sm text-gray-600">Fahrzeug gefunden. Bitte überprüfe die Daten.</p>
        </div>
        <div>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-[#E9041D] opacity-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>



      <div class="bg-white rounded-lg shadow-sm overflow-hidden">
        <div class="p-4">
          <form @submit.prevent="startCheck">
            <!-- Kennzeichen-Scan oder Eingabe -->
            <div class="space-y-4">
              <div>
                <!-- Scan-Button und manuelle Eingabe-Option -->
                <div v-if="!showLicensePlateInput" class="space-y-4">
                  <!-- Terminal-Auswahl -->
                  <div class="mb-4">
                    <label for="terminal" class="block text-sm font-medium text-gray-700 mb-1">{{ locationTypeLabelGerman }}</label>
                    
                    <!-- Standard Terminal Info -->
                    <div v-if="defaultTerminal && !showManualTerminalSelection" class="mb-3">
                      <div class="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div class="flex items-center justify-between">
                          <div class="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <p class="text-sm font-medium text-red-800">{{ defaultTerminal.name }}</p>
                              <p class="text-xs text-red-600">Standard-{{ locationTypeLabelGerman }} (aus Profil)</p>
                            </div>
                          </div>
                          <button
                            @click="showManualTerminalSelection = true"
                            class="text-sm text-red-600 hover:text-red-800 underline"
                          >
                            Ändern
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Manuelle Terminal-Auswahl -->
                    <div v-if="showManualTerminalSelection || !defaultTerminal">
                      <select 
                        id="terminal"
                        v-model="vehicleData.terminalId"
                        class="w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none h-12 text-base px-4 bg-white"
                        required
                        :disabled="isLoadingTerminals"
                      >
                        <option value="" disabled>Bitte wählen</option>
                        <option v-if="isLoadingTerminals" disabled>Lade Terminals...</option>
                        <option v-for="terminal in filteredTerminals" :key="terminal.id" :value="terminal.id" class="py-2">
                          {{ terminal.name }}
                        </option>
                      </select>
                      
                      <!-- Zurück zu Standard-Terminal Button -->
                      <div v-if="showManualTerminalSelection && defaultTerminal" class="mt-2">
                        <button
                          @click="useDefaultTerminal"
                          class="text-sm text-[#E9041D] hover:text-red-700 underline"
                        >
                          ← Zurück zu Standard-{{ locationTypeLabelGerman }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <button 
                    type="button"
                    @click="startScanning"
                    :disabled="!vehicleData.terminalId"
                    class="w-full py-4 px-4 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors text-base font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Kennzeichen scannen
                  </button>
                  
                  <div class="text-center">
                    <button 
                      type="button"
                      @click="showLicensePlateInput = true"
                      :disabled="!vehicleData.terminalId"
                      class="text-sm text-[#E9041D] hover:text-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Kennzeichen manuell erfassen
                    </button>
                  </div>
                </div>
                
                <!-- Kennzeichen-Input (wird angezeigt, wenn gescannt oder manuell eingegeben) -->
                <div v-if="showLicensePlateInput">
                  <!-- Terminal-Auswahl (ausgegraut und deaktiviert) -->
                  <div class="mb-4">
                    <label for="terminal-disabled" class="block text-sm font-medium text-gray-700 mb-1">{{ locationTypeLabelGerman }}</label>
                    <select 
                      id="terminal-disabled"
                      v-model="vehicleData.terminalId"
                      class="w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none h-12 text-base px-4 bg-gray-50 text-gray-700 border-gray-200 cursor-not-allowed"
                      disabled
                    >
                      <option v-for="terminal in filteredTerminals" :key="terminal.id" :value="terminal.id" class="py-2">
                        {{ terminal.name }}
                      </option>
                    </select>
                  </div>
                  
                  <!-- VIN-only toggle (DEV-15 15b) — dispatchers only: new cars
                       arrive without a plate, registered by Fahrgestellnummer. -->
                  <div v-if="isDispatcher && !searchCompleted" class="mb-3 flex items-center gap-2">
                    <input
                      id="vin-mode"
                      type="checkbox"
                      v-model="vinMode"
                      class="h-4 w-4 text-[#E9041D] rounded border-gray-300"
                    />
                    <label for="vin-mode" class="text-sm text-gray-700">Neuwagen ohne Kennzeichen (per VIN)</label>
                  </div>

                  <div v-if="!vinMode" class="mb-4">
                    <LicensePlateInput
                      ref="licensePlateInputRef"
                      v-model="vehicleData.licensePlate"
                      :disabled="searchCompleted"
                      required
                    />
                  </div>

                  <!-- VIN input (DEV-15 15b) -->
                  <div v-else class="mb-4">
                    <label for="vin" class="block text-sm font-medium text-gray-700 mb-1">Fahrgestellnummer (VIN)</label>
                    <input
                      id="vin"
                      v-model="vehicleData.vin"
                      :disabled="searchCompleted"
                      type="text"
                      maxlength="17"
                      placeholder="17-stellig"
                      class="w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none h-12 text-base px-4 uppercase disabled:bg-gray-50 disabled:cursor-not-allowed"
                    />
                    <p v-if="vehicleData.vin && !isValidVin" class="mt-1 text-xs text-red-600">
                      VIN muss 17-stellig sein (ohne I, O, Q).
                    </p>
                  </div>
                </div>
              </div>

              <!-- Weitere Fahrzeuginformationen -->
              <div v-if="showLicensePlateInput && isValidLicensePlate && searchCompleted" class="space-y-4">
                <div class="grid grid-cols-1 gap-4">
                  <div>
                    <label for="brand" class="block text-sm font-medium text-gray-700 mb-1">Marke</label>
                    <select
                      id="brand"
                      v-model="vehicleData.brand"
                      class="w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none h-12 text-base px-4"
                      :class="vehicleFound ? 'bg-gray-50 text-gray-700 border-gray-200 cursor-not-allowed' : 'bg-white'"
                      required
                      :disabled="vehicleFound || isLoadingBrands"
                    >
                      <option value="" disabled>Bitte wählen</option>
                      <option v-if="isLoadingBrands" disabled>Lade Marken...</option>
                      <option v-for="brand in filteredCarBrands" :key="brand.id" :value="brand.name">{{ brand.name }}</option>
                    </select>
                  </div>
                  <div>
                    <label for="model" class="block text-sm font-medium text-gray-700 mb-1">Modell</label>
                    <select
                      id="model"
                      v-model="vehicleData.model"
                      class="w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none h-12 text-base px-4"
                      :class="vehicleFound ? 'bg-gray-50 text-gray-700 border-gray-200 cursor-not-allowed' : 'bg-white'"
                      required
                      :disabled="vehicleFound || !vehicleData.brand || isLoadingModels"
                    >
                      <option value="" disabled>Bitte wählen</option>
                      <option v-if="isLoadingModels" disabled>Lade Modelle...</option>
                      <option v-for="model in carModels" :key="model.id" :value="model.name">{{ model.name }}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label for="rentalCompany" class="block text-sm font-medium text-gray-700 mb-1">Vermieter</label>
                  <select 
                    id="rentalCompany"
                    v-model="vehicleData.rentalCompanyId"
                    class="w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none h-12 text-base px-4"
                    :class="vehicleFound ? 'bg-gray-50 text-gray-700 border-gray-200 cursor-not-allowed' : 'bg-white'"
                    required
                    :disabled="isLoadingRentalCompanies || vehicleFound"
                  >
                    <option value="" disabled>Bitte wählen</option>
                    <option v-if="isLoadingRentalCompanies" disabled>Lade Vermieter...</option>
                    <option v-for="company in rentalCompanies" :key="company.id" :value="company.id" class="py-2">
                      {{ company.name }}
                    </option>
                  </select>
                </div>

                <!-- Kraftstoff-Auswahl -->
                <div>
                  <label for="fuel_type" class="block text-sm font-medium text-gray-700 mb-1">Kraftstoff</label>
                  <select 
                    id="fuel_type"
                    v-model="vehicleData.fuel_type"
                    class="w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none h-12 text-base px-4 bg-white"
                    required
                    :disabled="isSavingFuelType"
                    @change="onFuelTypeChanged"
                  >
                    <option value="" disabled>Bitte wählen</option>
                    <option value="petrol">Benzin</option>
                    <option value="diesel">Diesel</option>
                    <option value="electric">Elektro</option>
                  </select>
                  <!-- TRK-20: die Sorte ist am Fahrzeug gespeichert. Sie muss hier
                       korrigierbar sein, weil eine widersprechende Tankung
                       serverseitig abgelehnt wird. -->
                  <p v-if="vehicleFound" class="mt-1 text-xs text-gray-500">
                    <span v-if="isSavingFuelType">Wird am Fahrzeug gespeichert …</span>
                    <span v-else-if="fuelTypeSaved" class="text-green-700">Am Fahrzeug gespeichert.</span>
                    <span v-else>Gilt für das Fahrzeug, nicht nur für diesen Check-In.</span>
                  </p>
                </div>
                
                <!-- Custom Fields für neues Fahrzeug -->
                <div v-if="!vehicleFound && availableCustomFields.length > 0 && vehicleData.rentalCompanyId" class="mt-6">
                  <h3 class="text-sm font-medium text-gray-700 mb-3">Zusätzliche Informationen</h3>
                  <div class="space-y-4">
                    <div v-for="field in availableCustomFields" :key="field.id">
                      <label :for="`custom_field_${field.id}`" class="block text-sm font-medium text-gray-700 mb-1">
                        {{ field.field_label }}
                        <span v-if="field.is_required" class="text-red-500">*</span>
                      </label>
                      
                      <!-- Text Input -->
                      <input
                        v-if="field.field_type === 'text'"
                        :id="`custom_field_${field.id}`"
                        v-model="vehicleData.customFieldValues![field.id]"
                        type="text"
                        :placeholder="field.field_description || ''"
                        class="w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none h-12 text-base px-4 bg-white"
                        :required="field.is_required"
                      />
                      
                      <!-- Number Input -->
                      <input
                        v-else-if="field.field_type === 'number'"
                        :id="`custom_field_${field.id}`"
                        v-model="vehicleData.customFieldValues![field.id]"
                        type="number"
                        :placeholder="field.field_description || ''"
                        class="w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none h-12 text-base px-4 bg-white"
                        :required="field.is_required"
                      />
                      
                      <!-- Date Input -->
                      <input
                        v-else-if="field.field_type === 'date'"
                        :id="`custom_field_${field.id}`"
                        v-model="vehicleData.customFieldValues![field.id]"
                        type="date"
                        class="w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none h-12 text-base px-4 bg-white"
                        :required="field.is_required"
                      />
                      
                      <!-- Select Input -->
                      <select
                        v-else-if="field.field_type === 'select'"
                        :id="`custom_field_${field.id}`"
                        v-model="vehicleData.customFieldValues![field.id]"
                        class="w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none h-12 text-base px-4 bg-white"
                        :required="field.is_required"
                      >
                        <option value="" disabled>Bitte wählen</option>
                        <option 
                          v-for="option in (field.field_options && field.field_options.length > 0 ? field.field_options : [])" 
                          :key="option" 
                          :value="option"
                        >
                          {{ option }}
                        </option>
                      </select>
                      
                      <!-- Textarea Input -->
                      <textarea
                        v-else-if="field.field_type === 'textarea'"
                        :id="`custom_field_${field.id}`"
                        v-model="vehicleData.customFieldValues![field.id]"
                        :placeholder="field.field_description || ''"
                        rows="3"
                        class="w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none text-base px-4 py-3 bg-white"
                        :required="field.is_required"
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Custom Fields Section -->
              <div v-if="vehicleFound && customFields.length > 0" 
                   class="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-400 mt-6">
                <h3 class="font-medium text-blue-900 mb-3 flex items-center">
                  <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  Fahrzeugspezifische Informationen
                </h3>
                
                <div class="space-y-3">
                  <div v-for="field in customFields" :key="field.id" 
                       class="flex justify-between items-start bg-white rounded-md p-3">
                    <div class="flex-1">
                      <span class="text-sm font-medium text-gray-700">{{ field.field_label }}</span>
                      <p v-if="field.field_description" class="text-xs text-gray-500 mt-1">
                        {{ field.field_description }}
                      </p>
                    </div>
                    <div class="ml-4 text-right">
                      <span class="text-sm text-gray-900 font-medium">
                        {{ getFieldDisplayValue(field) || 'Nicht angegeben' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Lade-Animation während der Suche -->
              <div v-if="showLicensePlateInput && isValidLicensePlate && isSearching" class="py-6 flex flex-col items-center justify-center">
                <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-[#E9041D] mb-3"></div>
                <p class="text-gray-600">Suche nach Fahrzeug...</p>
              </div>
            </div>

            <!-- Navigation Buttons -->
            <div class="mt-6 flex flex-col space-y-3">
              <button 
                v-if="showLicensePlateInput && !searchCompleted"
                type="submit"
                :disabled="!isValidLicensePlate || isSearching"
                class="w-full px-4 py-4 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="isSearching">Suche läuft...</span>
                <span v-else>Fahrzeug suchen</span>
              </button>
              <button 
                v-if="searchCompleted"
                type="button"
                @click="createCheck"
                class="w-full px-4 py-4 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors text-base font-medium"
              >
                Check starten
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- License Plate Scanner Modal -->
    <LicensePlateScanner 
      :is-open="showScanner"
      :user-role="userRole"
      @close="showScanner = false"
      @scanned="handleScanned"
      @manual="handleManualEntry"
    />

    <!-- Active Check-in Modal -->
    <div v-if="showActiveCheckinModal && activeCheckinData" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg p-6 max-w-md w-full">
        <div class="mb-4">
          <div class="flex items-center justify-between mb-2">
            <h3 class="text-lg font-semibold text-gray-900">
              {{ isOpenCheckin ? 'Offener Check-In gefunden' : 'Fahrzeug bereits eingecheckt' }}
            </h3>
            <!-- Status badges -->
            <div class="flex gap-2">
              <!-- Key status badge -->
              <span v-if="activeCheckinData?.key?.status"
                :class="{
                  'bg-yellow-100 text-yellow-800': activeCheckinData.key.status === 'ready',
                  'bg-blue-100 text-blue-800': activeCheckinData.key.status === 'in_service',
                  'bg-green-100 text-green-800': activeCheckinData.key.status === 'handed_over',
                  'bg-purple-100 text-purple-800': activeCheckinData.key.status === 'in_list',
                  'bg-gray-100 text-gray-800': activeCheckinData.key.status === 'created'
                }"
                class="px-2 py-1 text-xs font-medium rounded-full">
                Schlüssel: {{
                  activeCheckinData.key.status === 'ready' ? 'Bereit' :
                  activeCheckinData.key.status === 'in_service' ? 'In Bearbeitung' :
                  activeCheckinData.key.status === 'handed_over' ? 'Übergeben' :
                  activeCheckinData.key.status === 'in_list' ? 'In Liste' :
                  activeCheckinData.key.status === 'created' ? 'Erstellt' :
                  activeCheckinData.key.status
                }}
              </span>
              <!-- Check-in status badge -->
              <span v-if="activeCheckinData?.checkin?.status"
                :class="{
                  'bg-blue-100 text-blue-800': activeCheckinData.checkin.status === 'open',
                  'bg-yellow-100 text-yellow-800': activeCheckinData.checkin.status === 'progress',
                  'bg-gray-100 text-gray-800': activeCheckinData.checkin.status === 'closed'
                }"
                class="px-2 py-1 text-xs font-medium rounded-full">
                Check-In: {{
                  activeCheckinData.checkin.status === 'open' ? 'Offen' :
                  activeCheckinData.checkin.status === 'progress' ? 'In Bearbeitung' :
                  activeCheckinData.checkin.status === 'closed' ? 'Abgeschlossen' :
                  activeCheckinData.checkin.status
                }}
              </span>
            </div>
          </div>
          <p class="text-sm text-gray-600">
            {{ isOpenCheckin
              ? `Für dieses Fahrzeug (${vehicleData.licensePlate}) wurde bereits ein Check-In gestartet.`
              : `Dieses Fahrzeug (${vehicleData.licensePlate}) hat bereits einen aktiven Check-in.`
            }}
          </p>
        </div>

        <!-- Warning: Another user's checkin -->
        <div v-if="!isOwnCheckin && isOpenCheckin" class="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
          <div class="flex items-start">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-orange-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div class="text-sm">
              <p class="font-semibold text-orange-800">Anderer Benutzer</p>
              <p class="text-orange-700">
                Dieser Check-In wurde von <strong>{{ activeCheckinData?.ownership?.owner_name }}</strong> gestartet
                <span v-if="activeCheckinData?.ownership?.inactive_minutes">
                  (inaktiv seit {{ activeCheckinData.ownership.inactive_minutes }} Min.)
                </span>
              </p>
              <p class="text-orange-600 text-xs mt-1">
                Wenn du fortfährst, übernimmst du diesen Check-In.
              </p>
            </div>
          </div>
        </div>

        <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div class="flex">
            <svg class="h-5 w-5 text-blue-400 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div class="text-sm text-blue-800">
              <p class="font-semibold">Aktuelle Services:</p>
              <ul class="mt-1 list-disc list-inside" v-if="activeCheckinData.checkin?.services?.list">
                <li v-for="service in activeCheckinData.checkin.services.list" :key="service.id">
                  {{ service.name }}
                  <span v-if="service.status === 'completed'" class="text-green-600">(✓)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="flex flex-col space-y-3">
          <!-- Key handed over - highest priority -->
          <div 
            v-if="activeCheckinData?.key?.status === 'handed_over'"
            class="w-full px-4 py-3 bg-gray-400 text-white rounded-lg text-center font-medium"
          >
            Schlüssel bereits übergeben
          </div>
          
          <!-- Key in list - cannot modify -->
          <div 
            v-else-if="activeCheckinData?.key?.status === 'in_list'"
            class="w-full px-4 py-3 bg-blue-500 text-white rounded-lg text-center font-medium"
          >
            Schlüssel ist Teil einer Sammelliste
          </div>
          
          <!-- Cannot add services based on backend validation -->
          <div 
            v-else-if="!activeCheckinData?.can_add_services"
            class="w-full px-4 py-3 bg-gray-400 text-white rounded-lg text-center font-medium"
          >
            {{ activeCheckinData?.block_reason || 'Services können nicht hinzugefügt werden' }}
          </div>
          
          <!-- Can add services - show appropriate message and button -->
          <template v-else-if="activeCheckinData?.can_add_services">
            <!-- Show warning if key is ready -->
            <div v-if="activeCheckinData?.key?.status === 'ready'" class="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p class="text-sm text-yellow-800">
                <svg xmlns="http://www.w3.org/2000/svg" class="inline h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                {{ activeCheckinData?.action_suggestion || 'Schlüssel ist bereit zur Übergabe. Das Hinzufügen von Services setzt den Status zurück.' }}
              </p>
            </div>
            
            <!-- Open checkin - show resume/takeover button prominently -->
            <button
              v-if="isOpenCheckin"
              @click="handleResumeCheckin"
              :class="isOwnCheckin
                ? 'bg-[#E9041D] hover:bg-red-700'
                : 'bg-orange-600 hover:bg-orange-700'"
              class="w-full px-4 py-3 text-white rounded-lg transition-colors font-medium flex items-center justify-center"
            >
              <svg v-if="isOwnCheckin" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              {{ isOwnCheckin ? 'Check-In fortsetzen' : 'Check-In übernehmen' }}
            </button>

            <!-- Button with conditional styling based on key status -->
            <button
              v-else-if="activeCheckinData?.key?.status === 'ready' || activeCheckinData?.key?.status === 'in_service'"
              @click="handleAddServices"
              class="w-full px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium"
            >
              Services hinzufügen (Mitarbeiter-Berechtigung)
            </button>

            <!-- Normal state - can add services without warning -->
            <button
              v-else
              @click="handleAddServices"
              class="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              Services hinzufügen
            </button>
          </template>

          <!-- TRK-83: hand the car over right here (Werkstatt at Enterprise). The
               backend says whether a destination exists for this role and car. -->
          <button
            v-if="activeCheckinData?.quick_checkout?.available && !isOpenCheckin"
            @click="handleQuickCheckout"
            class="w-full px-4 py-3 bg-gray-900 text-white rounded-lg hover:bg-black transition-colors font-medium flex items-center justify-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Fahrzeug rausgeben (Quick Checkout)
          </button>
          
          <button
            @click="handleCancelActiveCheckin"
            class="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>

    <!-- TRK-72: back from the workshop → only Umparken -->
    <div v-if="showWorkshopReturnModal && workshopReturn" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg p-6 max-w-md w-full">
        <div class="flex items-start mb-3">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-amber-600 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Fahrzeug kam aus der Werkstatt</h3>
            <p class="text-sm text-gray-600 mt-1">{{ workshopReturn.message }}</p>
          </div>
        </div>
        <div class="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-900">
          Bitte nur <span class="font-semibold">Umparken</span> erfassen. Ein Turnaround ist nach
          der Werkstatt nicht berechenbar und wird im Review gemeldet.
        </div>
        <div class="flex flex-col space-y-2">
          <button
            @click="confirmWorkshopReturn"
            class="w-full px-4 py-3 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            Verstanden, Check-In starten
          </button>
          <button
            @click="cancelWorkshopReturn"
            class="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>

    <!-- Duplicate Turnaround (DEV-220) -->
    <DuplicateTurnaroundModal
      :show="showDuplicateTurnaroundModal"
      :data="duplicateTurnaroundData"
      v-model:reason="duplicateTurnaroundReason"
      :error="duplicateTurnaroundError"
      :is-confirming="isConfirmingDuplicateTurnaround"
      :needs-reason="duplicateTurnaroundNeedsReason"
      :can-confirm="canConfirmDuplicateTurnaround"
      @confirm="confirmDuplicateTurnaround"
      @close="closeDuplicateTurnaroundModal"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, nextTick, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '../api/axios'
import { useAuth } from '../composables/useAuth'
import LicensePlateInput from '../components/LicensePlateInput.vue'
import LicensePlateScanner from '../components/LicensePlateScanner.vue'
import DuplicateTurnaroundModal from '../components/DuplicateTurnaroundModal.vue'
import { useDuplicateTurnaround } from '../composables/useDuplicateTurnaround'
import type { CustomField } from '../types/customFields'
import { normalizePlateInput } from '../utils/licensePlate'

interface VehicleData {
  licensePlate: string
  vin: string
  brand: string
  model: string
  rentalCompanyId: string
  terminalId: string
  fuel_type: string
  customFieldValues?: { [key: string]: string }
}

interface RentalCompany {
  id: number
  name: string
}

interface Terminal {
  id: number
  name: string
  location_type: {
    id: number
    name: string
    slug: string
  }
}

interface User {
  id: number
  name: string
  email: string
  email_verified_at: string | null
  created_at: string
  updated_at: string
  role_name: string
  airport_id?: number // Optional, da es möglicherweise nicht immer vorhanden ist
}

interface CurrentUser {
  id: number | null
  airport_id: number | null
}

const router = useRouter()
const showLicensePlateInput = ref(false)
const showScanner = ref(false)
const isScanned = ref(false)

// Modal state for active check-in
const showActiveCheckinModal = ref(false)
const activeCheckinData = ref<any>(null)

// TRK-72: the car is back from the own workshop / service center. Shown
// BEFORE the new check-in is created, so the checker reads it before picking
// services; the backend guard asks for a reason if a Turnaround follows anyway.
const workshopReturn = ref<{ message: string; previous_checkin_at: string; workshop_services: string[] } | null>(null)
const showWorkshopReturnModal = ref(false)
let pendingCheckinPayload: Record<string, any> | null = null

// Check if the active checkin is open/in-progress (not yet completed)
const isOpenCheckin = computed(() => {
  const status = activeCheckinData.value?.checkin?.status
  return status === 'open' || status === 'progress'
})

// Check if the active checkin belongs to the current user
const isOwnCheckin = computed(() => {
  return activeCheckinData.value?.ownership?.is_own_checkin ?? true
})

// DEV-220: duplicate-turnaround guard. Shared with ChecksServicesView (which
// hits the same guard on add-services) — state + retry live in the composable.
const {
  showModal: showDuplicateTurnaroundModal,
  data: duplicateTurnaroundData,
  reason: duplicateTurnaroundReason,
  modalError: duplicateTurnaroundError,
  isConfirming: isConfirmingDuplicateTurnaround,
  needsReason: duplicateTurnaroundNeedsReason,
  canConfirm: canConfirmDuplicateTurnaround,
  submitGuarded: submitCheckinGuarded,
  confirm: confirmDuplicateTurnaround,
  close: closeDuplicateTurnaroundModal
} = useDuplicateTurnaround<Record<string, any>>({
  // submitCheckin is declared further down, so wrap it (no TDZ at setup time).
  submit: payload => submitCheckin(payload),
  onBlocked: message => alert(message),
  confirmErrorMessage: 'Fehler beim Anlegen des Check-Ins. Bitte die Disposition kontaktieren.'
})

const { state: authState, fetchUser, getSelectedTerminal, getSelectedLocation, isChecker, isDisponent } = useAuth()
const isLoadingUser = ref(false)
const currentUser = ref<CurrentUser>({
  id: null,
  airport_id: null
})

// Role-based computed properties — follow the ACTIVE role (from useAuth), not
// the static primary role, so a role switch is reflected here too.
const isDispatcher = isDisponent
const userRole = computed(() => isDispatcher.value ? 'dispatcher' : 'checker')
const locationTypeLabelGerman = computed(() => isDispatcher.value ? 'Service Center' : 'Terminal')

// Referenz zur LicensePlateInput-Komponente
const licensePlateInputRef = ref()

// Terminal-Auswahl Variablen
const showManualTerminalSelection = ref(false)

const vehicleData = ref<VehicleData>({
  licensePlate: '',
  vin: '',
  brand: '',
  model: '',
  rentalCompanyId: '',
  terminalId: '',
  fuel_type: '',
  customFieldValues: {}
})

// VIN-only intake (DEV-15 15b): dispatchers register plateless new cars by VIN.
const vinMode = ref(false)
const isValidVin = computed(() => /^[A-HJ-NPR-Z0-9]{17}$/i.test((vehicleData.value.vin || '').trim()))

// Computed property für die Standard-Location aus dem Profil (Terminal oder Service Center)
const defaultLocation = computed(() => {
  // First try the enhanced location system (supports both terminals and service centers)
  const selectedLocation = getSelectedLocation()
  if (selectedLocation && allLocations.value.length > 0) {
    // Search in all locations first, then filter by role-appropriate type
    const location = allLocations.value.find(loc => loc.id === selectedLocation.id)
    if (location) {
      // Check if the location type matches user role
      const isValidForRole = (isDispatcher.value && location.location_type.slug === 'servicecenter') ||
                            (isChecker.value && location.location_type.slug === 'terminal')
      
      if (isValidForRole) {
        console.log('Using enhanced location system:', selectedLocation.type, selectedLocation.name)
        return location
      }
    }
  }
  
  // Fallback to legacy terminal system for backward compatibility
  const selectedTerminalId = getSelectedTerminal()
  if (selectedTerminalId && allLocations.value.length > 0) {
    const location = allLocations.value.find(loc => loc.id === selectedTerminalId)
    if (location) {
      // For legacy system, only allow terminals for checkers
      if (isChecker.value && location.location_type.slug === 'terminal') {
        console.log('Using legacy terminal system:', location.name)
        return location
      }
    }
  }
  
  return null
})

// Keep backward compatibility with existing code that expects defaultTerminal
const defaultTerminal = computed(() => defaultLocation.value)

// Funktion zum Verwenden der Standard-Location (Terminal oder Service Center)
const useDefaultTerminal = () => {
  if (defaultLocation.value) {
    vehicleData.value.terminalId = defaultLocation.value.id.toString()
    showManualTerminalSelection.value = false
    const selectedLocation = getSelectedLocation()
    const locationType = selectedLocation ? selectedLocation.type : 'terminal'
    console.log(`Standard-${locationType} verwendet:`, defaultLocation.value.name)
  }
}

// Computed property für die Validierung des Kennzeichens und Terminals
const isValidLicensePlate = computed(() => {
  // Wenn wir im ersten Schritt sind (Terminal-Auswahl), brauchen wir nur das Terminal
  if (!showLicensePlateInput.value) {
    return vehicleData.value.terminalId !== ''
  }
  // VIN-only intake (DEV-15 15b): plate input is replaced by a valid 17-char VIN.
  if (vinMode.value) {
    return isValidVin.value && vehicleData.value.terminalId !== ''
  }
  // Wenn wir im zweiten Schritt sind (Kennzeichen-Eingabe), brauchen wir beides
  if (!licensePlateInputRef.value) return false
  return licensePlateInputRef.value.isValidFormat && vehicleData.value.terminalId !== ''
})

// Flag für die Suche nach Fahrzeugen
const isSearching = ref(false)
const vehicleFound = ref(false)
// TRK-20: für die Korrektur der Kraftstoffart am gefundenen Fahrzeug
const foundVehicleId = ref<number | null>(null)
const isSavingFuelType = ref(false)
const fuelTypeSaved = ref(false)
const lastSavedFuelType = ref<string>('')
const searchCompleted = ref(false)
const customFields = ref<CustomField[]>([])

// Vermieterliste
const rentalCompanies = ref<RentalCompany[]>([])
const isLoadingRentalCompanies = ref(false)

// Custom Fields für ausgewählte Autovermietung
const availableCustomFields = ref<any[]>([])
const isLoadingCustomFields = ref(false)

// Location lists (terminals and service centers)
const terminals = ref<Terminal[]>([])
const allLocations = ref<Terminal[]>([])
const isLoadingTerminals = ref(false)

// Im <script setup lang="ts"> Bereich:
const carBrands = ref<any[]>([])
const filteredCarBrands = computed(() => (carBrands.value ?? []).filter(b => b.is_active && b.car_models_count > 0))
const carModels = ref<any[]>([])
const isLoadingBrands = ref(false)
const isLoadingModels = ref(false)

const fetchCarBrands = async () => {
  isLoadingBrands.value = true
  try {
    const response = await api.get('/api/car-brands')
    carBrands.value = Array.isArray(response.data) ? response.data : response.data.data
    if (!carBrands.value || carBrands.value.length === 0) {
      console.error('carBrands ist leer! Response:', response)
    }
  } catch (e) {
    console.error('Fehler beim Laden der Marken:', e)
  } finally {
    isLoadingBrands.value = false
  }
}

const fetchCarModels = async (brand: string) => {
  isLoadingModels.value = true
  try {
    const response = await api.get(`/api/car-models?brand=${encodeURIComponent(brand)}`)
    carModels.value = Array.isArray(response.data) ? response.data : response.data.data
  } catch (e) {
    console.error('Fehler beim Laden der Modelle:', e)
    carModels.value = []
  } finally {
    isLoadingModels.value = false
  }
}

watch(() => vehicleData.value.brand, (brand, oldBrand) => {
  // Nur das Modell zurücksetzen, wenn sich die Marke geändert hat und kein Fahrzeug gefunden wurde
  if (brand !== oldBrand && !vehicleFound.value) {
    vehicleData.value.model = ''
  }
  
  if (brand) {
    fetchCarModels(brand)
  } else {
    carModels.value = []
  }
})

// Ersetze die fetchUserData Funktion
const fetchUserData = async () => {
  isLoadingUser.value = true
  
  try {
    // Versuche zuerst, die Benutzerdaten zu laden, falls sie noch nicht vorhanden sind
    if (!authState.value.user) {
      console.log('Benutzerdaten nicht vorhanden, lade sie...')
      await fetchUser()
    }
    
    console.log('Verwende vorhandene Benutzerdaten:', authState.value.user)
    
    const user = authState.value.user as User
    if (user?.id && user?.airport_id) {
      currentUser.value = {
        id: user.id,
        airport_id: user.airport_id
      }
      console.log('Aktueller Benutzer:', currentUser.value)
      
      // Nachdem wir die Benutzerdaten haben, laden wir die Flughafendaten
      console.log('Benutzer hat Airport ID:', currentUser.value.airport_id)
      if (currentUser.value.airport_id !== null) {
        await fetchAirportData(currentUser.value.airport_id)
      } else {
        console.error('Keine gültige Airport ID gefunden')
      }
    } else {
      console.error('Benutzerdaten sind unvollständig oder haben nicht das erwartete Format')
      console.error('Erhaltene Daten:', authState.value.user)
    }
  } catch (error) {
    console.error('Fehler beim Verarbeiten der Benutzerdaten:', error)
  } finally {
    isLoadingUser.value = false
  }
}

// Flughafendaten abrufen
const fetchAirportData = async (airportId: number) => {
  isLoadingTerminals.value = true
  
  try {
    console.log('Lade Flughafendaten für Airport ID:', airportId)
    // Spezifischen Airport abrufen
    const airportResponse = await api.get(`/api/airports/${airportId}`)
    console.log('Flughafendaten erhalten:', airportResponse.data)
    
    // Die Daten sind in response.data.data
    const airport = airportResponse.data.data
    console.log('Airport Objekt:', airport)
    
    if (airport.locations && Array.isArray(airport.locations)) {
      console.log('Locations gefunden:', airport.locations)
      
      // Debug: Zeige alle location_types an
      airport.locations.forEach((location: Terminal) => {
        console.log(`Location ${location.id}:`, location.name, 'Location Type:', location.location_type)
      })
      
      // Store all locations first
      allLocations.value = airport.locations
      
      // Filter locations based on user role
      if (isDispatcher.value) {
        // Dispatchers see service centers
        terminals.value = airport.locations.filter((location: Terminal) => {
          console.log(`Prüfe Location ${location.id} für Dispatcher:`, location.name, 'Location Type:', location.location_type.slug)
          return location.location_type.slug === 'servicecenter'
        })
      } else {
        // Checkers see terminals (default behavior)
        terminals.value = airport.locations.filter((location: Terminal) => {
          console.log(`Prüfe Location ${location.id} für Checker:`, location.name, 'Location Type:', location.location_type.slug)
          return location.location_type.slug === 'terminal'
        })
      }
      
      console.log('Gefilterte Terminals:', terminals.value)
      
      // Debug: Zeige die Anzahl der gefundenen Locations
      const locationType = isDispatcher.value ? 'Service Centers' : 'Terminals'
      console.log(`Anzahl gefundener ${locationType}: ${terminals.value.length}`)
      
      // Setze die Standard-Location automatisch, wenn verfügbar
      if (defaultLocation.value && !vehicleData.value.terminalId) {
        vehicleData.value.terminalId = defaultLocation.value.id.toString()
        const selectedLocation = getSelectedLocation()
        const locationType = selectedLocation ? selectedLocation.type : 'terminal'
        console.log(`Standard-${locationType} automatisch gesetzt:`, defaultLocation.value.name)
      }
    } else {
      console.error('Keine Locations im Airport-Objekt gefunden')
      console.error('Vollständiges Airport-Objekt:', airport)
    }
  } catch (error) {
    console.error('Fehler beim Abrufen der Flughafendaten:', error)
  } finally {
    isLoadingTerminals.value = false
  }
}

// Vermieterliste von der API abrufen
const fetchRentalCompanies = async () => {
  isLoadingRentalCompanies.value = true
  
  try {
    const response = await api.get('/api/rental-companies')
    rentalCompanies.value = response.data.data
  } catch (error) {
    console.error('Fehler beim Abrufen der Vermieterliste:', error)
  } finally {
    isLoadingRentalCompanies.value = false
  }
}

// Funktion zum Laden der Custom Fields einer Autovermietung
const fetchCustomFields = async (rentalCompanyId: string) => {
  if (!rentalCompanyId) {
    availableCustomFields.value = []
    return
  }
  
  isLoadingCustomFields.value = true
  
  try {
    const response = await api.get(`/api/rental-companies/${rentalCompanyId}/custom-fields/active`)
    availableCustomFields.value = response.data || []
    
    // Initialisiere custom field values
    if (!vehicleData.value.customFieldValues) {
      vehicleData.value.customFieldValues = {}
    }
    
    // Fülle vorhandene Werte für Custom Fields ein (falls bereits vorhanden)
    availableCustomFields.value.forEach(field => {
      if (!(field.id in vehicleData.value.customFieldValues!)) {
        vehicleData.value.customFieldValues![field.id] = ''
      }
    })
    
  } catch (error) {
    console.error('Fehler beim Abrufen der Custom Fields:', error)
    availableCustomFields.value = []
  } finally {
    isLoadingCustomFields.value = false
  }
}

// Watch für Änderungen der rentalCompanyId
watch(() => vehicleData.value.rentalCompanyId, (newValue) => {
  if (newValue) {
    fetchCustomFields(newValue)
  } else {
    availableCustomFields.value = []
    vehicleData.value.customFieldValues = {}
  }
})


onMounted(async () => {
  try {
    console.log('Component mounted, initializing...')
    
    // Lade Benutzerdaten
    await fetchUserData()
    console.log('User data loaded')
    
    // Lade Vermieterliste
    await fetchRentalCompanies()
    console.log('Rental companies loaded')
    await fetchCarBrands()
    console.log('Car brands loaded')
  } catch (error) {
    console.error('Error during initialization:', error)
  }
})

const startScanning = () => {
  showScanner.value = true
}

// Handler functions for the scanner
const handleScanned = (licensePlate: string) => {
  // OCR liefert Umlaut-Form (z.B. RÜD-…) — auf kanonische UE-Form falten (DEV-215)
  vehicleData.value.licensePlate = normalizePlateInput(licensePlate)
  showLicensePlateInput.value = true
  isScanned.value = true
  console.log('License plate detected:', licensePlate)
}

// TRK-20: Kraftstoffart am Fahrzeug korrigieren.
// Nur bei einem bestehenden Fahrzeug relevant — bei einem neuen wird die Sorte
// beim Anlegen mitgeschickt. Online-only wie der gesamte Check-In-Start (die
// Fahrzeugsuche braucht ohnehin das Netz); diese View hatte nie einen
// Offline-Pfad, es wird also keiner entfernt (§0.9).
const onFuelTypeChanged = async () => {
  if (!vehicleFound.value || !foundVehicleId.value || !vehicleData.value.fuel_type) return

  const previous = lastSavedFuelType.value
  if (previous === vehicleData.value.fuel_type) return

  isSavingFuelType.value = true
  fuelTypeSaved.value = false
  try {
    await api.patch(`/api/checker/vehicles/${foundVehicleId.value}/fuel-type`, {
      fuel_type: vehicleData.value.fuel_type
    })
    lastSavedFuelType.value = vehicleData.value.fuel_type
    fuelTypeSaved.value = true
  } catch (e) {
    // Zurücksetzen, damit das Feld nicht etwas anderes behauptet als die DB.
    vehicleData.value.fuel_type = previous
    alert('Die Kraftstoffart konnte nicht gespeichert werden. Bitte erneut versuchen.')
    console.error('Fehler beim Speichern der Kraftstoffart:', e)
  } finally {
    isSavingFuelType.value = false
  }
}

const handleManualEntry = () => {
  showLicensePlateInput.value = true
  isScanned.value = false
  // Focus the license plate input after a small delay
  nextTick(() => {
    if (licensePlateInputRef.value) {
      licensePlateInputRef.value.focus()
    }
  })
}

// Old captureImage function removed - functionality now handled by LicensePlateScanner component

// Backend API now handles all license plate formatting correctly

// Aufräumen beim Unmount  
onUnmounted(() => {
  // Cleanup handled by LicensePlateScanner component
})

const startCheck = async () => {
  // VIN-only intake (DEV-15 15b): a VIN replaces the plate as the search key.
  if (vinMode.value) {
    if (!isValidVin.value) {
      return
    }
  } else if (!vehicleData.value.licensePlate) {
    // Prüfen, ob ein Kennzeichen eingegeben wurde
    return
  }

  // Prüfen, ob die Eingabe (Kennzeichen oder VIN) gültig ist
  if (!isValidLicensePlate.value) {
    return
  }

  // Suche nach dem Fahrzeug
  isSearching.value = true
  searchCompleted.value = false

  try {
    const response = await api.post('/api/vehicles/search', vinMode.value
      ? { vin: vehicleData.value.vin.trim().toUpperCase() }
      : { license_plate: vehicleData.value.licensePlate }
    )
    
    console.log('API-Antwort erhalten:', response.data)
    
    // Speichere das aktuelle Terminal
    const currentTerminalId = vehicleData.value.terminalId
    
    // Prüfe, ob Fahrzeugdaten im data-Array vorhanden sind
    if (response.data.data && response.data.data.length > 0) {
      const vehicle = response.data.data[0]
      vehicleFound.value = true
      foundVehicleId.value = vehicle.id ?? null
      fuelTypeSaved.value = false
      lastSavedFuelType.value = vehicle.fuel_type || ''
      
      // Aktualisiere nur die Fahrzeugdaten, behalte das Terminal bei
      vehicleData.value = {
        ...vehicleData.value,  // Behalte bestehende Werte
        brand: vehicle.brand,
        model: vehicle.model,
        rentalCompanyId: vehicle.rental_company?.id || vehicle.rental_company_id || '',
        terminalId: currentTerminalId,  // Behalte das ausgewählte Terminal bei
        fuel_type: vehicle.fuel_type || '',  // Übernehme den Kraftstoff, falls vorhanden
        customFieldValues: vehicleData.value.customFieldValues || {}
      }
      
      // Load custom fields if available
      if (response.data.custom_fields) {
        customFields.value = response.data.custom_fields
      }
      
      // Wenn ein Fahrzeug gefunden wurde, lade die Modelle für die Marke
      if (vehicle.brand) {
        await fetchCarModels(vehicle.brand)
      }
    } else {
      // Kein Fahrzeug gefunden, behalte das Terminal bei
      vehicleFound.value = false
      foundVehicleId.value = null
      vehicleData.value = {
        ...vehicleData.value,  // Behalte bestehende Werte
        brand: '',
        model: '',
        rentalCompanyId: '',
        terminalId: currentTerminalId,  // Behalte das ausgewählte Terminal bei
        fuel_type: '',  // Setze den Kraftstoff zurück
        customFieldValues: {}
      }
    }
  } catch (err: any) {
    console.error('Fehler bei der Suche nach dem Fahrzeug:', err)
    console.error('Fehlerdetails:', err.response?.data)
    vehicleFound.value = false
    foundVehicleId.value = null
  } finally {
    isSearching.value = false
    searchCompleted.value = true
  }
}

// Validierungsfunktion für Kennzeichen - wird in LicensePlateInput-Komponente verwendet
// Die LicensePlateInput-Komponente hat bereits EU/DE Unterscheidung implementiert
// const validateLicensePlate = (plate: string): boolean => {
//   // Reguläre Ausdrücke für deutsche Kennzeichen:
//   // - Ortskürzel-BuchstabenZahlen: ^[A-Z]{1,3}-[A-Z]{1,2}\d{1,4}$
//   // - Behördenkennzeichen: ^[A-Z]{1,3}-[A-Z]-[A-Z]-\d{1,4}$
//   // - Behördenkennzeichen ohne Bindestrich: ^[A-Z]{1,3}-[A-Z]{2}\d{1,4}$
//   const standardRegex = /^[A-Z]{1,3}-[A-Z]{1,2}\d{1,4}$/
//   const specialRegex = /^[A-Z]{1,3}-[A-Z]-[A-Z]-\d{1,4}$/
//   const specialNoDashRegex = /^[A-Z]{1,3}-[A-Z]{2}\d{1,4}$/
//   
//   return standardRegex.test(plate) || specialRegex.test(plate) || specialNoDashRegex.test(plate)
// }

// Funktion zum Erstellen eines Checks
const createCheck = async () => {
  // VIN-only intake (DEV-15 15b): require a valid VIN instead of a plate.
  const identifierOk = vinMode.value ? isValidVin.value : !!vehicleData.value.licensePlate
  // Prüfen, ob alle erforderlichen Felder ausgefüllt sind
  if (!identifierOk || !vehicleData.value.terminalId ||
      !vehicleData.value.brand || !vehicleData.value.model || !vehicleData.value.rentalCompanyId || !vehicleData.value.fuel_type) {
    console.error('Nicht alle erforderlichen Felder sind ausgefüllt')
    return
  }
  
  let vehicleId: number | null = null  // Move vehicleId declaration outside try block
  
  try {
    
    // Wenn kein Fahrzeug gefunden wurde, erstelle ein neues Fahrzeug
    if (!vehicleFound.value) {
      console.log('Erstelle neues Fahrzeug...')
      
      // Bereite Custom Field Values vor
      const customFieldValues: { [key: string]: string } = {}
      if (vehicleData.value.customFieldValues) {
        // Filtere leere Werte heraus
        Object.entries(vehicleData.value.customFieldValues).forEach(([fieldId, value]) => {
          // Convert value to string and check if it's not empty
          const stringValue = String(value || '').trim()
          if (stringValue !== '') {
            customFieldValues[fieldId] = stringValue
          }
        })
      }
      
      const vehicleData_payload = {
        // VIN-only intake (DEV-15 15b): send the VIN and no plate; otherwise plate.
        ...(vinMode.value
          ? { vin: vehicleData.value.vin.trim().toUpperCase() }
          : { license_plate: vehicleData.value.licensePlate }),
        brand: vehicleData.value.brand,
        model: vehicleData.value.model,
        rental_company_id: vehicleData.value.rentalCompanyId,
        fuel_type: vehicleData.value.fuel_type,
        ...(Object.keys(customFieldValues).length > 0 && { custom_field_values: customFieldValues })
      }
      
      console.log('Sende Fahrzeugdaten:', vehicleData_payload)
      const vehicleResponse = await api.post('/api/vehicles', vehicleData_payload)
      console.log('Fahrzeug erstellt:', vehicleResponse.data)
      // Robust: Unterstütze verschiedene API-Response-Formate
      vehicleId = vehicleResponse.data.id 
        ?? vehicleResponse.data.data?.id 
        ?? (Array.isArray(vehicleResponse.data.data) ? vehicleResponse.data.data[0]?.id : undefined)
      console.log('Neue vehicleId:', vehicleId)
    } else {
      // Wenn ein Fahrzeug gefunden wurde, suche nach der ID
      console.log('Suche nach Fahrzeug-ID...')
      const searchResponse = await api.post('/api/vehicles/search', vinMode.value
        ? { vin: vehicleData.value.vin.trim().toUpperCase() }
        : { license_plate: vehicleData.value.licensePlate }
      )
      
      if (searchResponse.data.data && searchResponse.data.data.length > 0) {
        vehicleId = searchResponse.data.data[0].id
        console.log('Fahrzeug-ID gefunden:', vehicleId)
      } else {
        console.error('Fahrzeug nicht gefunden, obwohl es vorher gefunden wurde')
        return
      }
    }
    
    // Check for existing active check-in before creating a new one
    let workshopReturnFromStatus: any = null
    if (vehicleId) {
      console.log('Prüfe auf aktiven Check-in für Fahrzeug:', vehicleId)
      const statusResponse = await api.get(`/api/checker/checkins/vehicle/${vehicleId}/status`)
      workshopReturnFromStatus = statusResponse.data?.workshop_return ?? null

      if (statusResponse.data.has_active_checkin) {
        console.log('Aktiver Check-in gefunden:', statusResponse.data)
        
        // Show modal to ask if user wants to add services
        if (statusResponse.data.can_add_services) {
          showActiveCheckinModal.value = true
          activeCheckinData.value = statusResponse.data
          return // Stop here and wait for user decision
        } else {
          // Cannot add services - show blocking reason
          const reason = statusResponse.data.block_reason || 'Services können nicht hinzugefügt werden'
          alert(reason)
          return
        }
      }
    }
    
    // Erstelle einen Check-In (nur wenn kein aktiver Check-in existiert)
    if (vehicleId && currentUser.value.id) {
      const payload = {
        vehicle_id: vehicleId,
        user_id: currentUser.value.id,
        checkin_location_id: vehicleData.value.terminalId
      }

      // TRK-72: back from the workshop → say so first, create on "Verstanden".
      if (workshopReturnFromStatus) {
        workshopReturn.value = workshopReturnFromStatus
        pendingCheckinPayload = payload
        showWorkshopReturnModal.value = true
        return
      }

      console.log('Erstelle Check-In...')
      // DEV-220: a duplicate turnaround is caught here (modal / hard block),
      // everything else bubbles up to the catch below.
      await submitCheckinGuarded(payload)
    } else {
      console.error('Fehlende Daten für Check-In:', { 
        vehicleId, 
        userId: currentUser.value.id, 
        terminalId: vehicleData.value.terminalId 
      })
    }
  } catch (err: any) {
    console.error('Fehler beim Erstellen des Checks:', err)

    // Handle specific error cases
    if (err.response?.status === 404 && err.response?.config?.url?.includes('/status')) {
      // Status endpoint doesn't exist yet - create check-in normally
      console.log('Status endpoint nicht verfügbar, erstelle Check-in normal')
      
      if (vehicleId && currentUser.value.id) {
        try {
          // DEV-220: duplicate turnaround also possible on this fallback path
          await submitCheckinGuarded({
            vehicle_id: vehicleId,
            user_id: currentUser.value.id,
            checkin_location_id: vehicleData.value.terminalId
          })
        } catch (innerError: any) {
          console.error('Fehler beim Erstellen des Check-ins:', innerError)
          alert('Fehler beim Erstellen des Check-ins. Bitte versuchen Sie es erneut.')
        }
      }
    } else if (err.response?.status === 409) {
      // Backend detected an active checkin (race condition protection)
      alert(err.response?.data?.message || 'Dieses Fahrzeug hat bereits einen aktiven Check-In.')
    } else {
      alert('Fehler beim Erstellen des Checks. Bitte versuchen Sie es erneut.')
    }
  }
}

// DEV-220: issues the create call. Also used as the confirmation retry by
// useDuplicateTurnaround, so the whole success handling lives in here.
const submitCheckin = async (payload: Record<string, any>) => {
  const checkInResponse = await api.post('/api/checker/checkins', payload)

  console.log('Check-In erstellt:', checkInResponse.data)

  // Weiterleitung zur Check-Prozess-Seite
  if (checkInResponse.data?.data?.id) {
    const checkInId = checkInResponse.data.data.id
    console.log('Weiterleitung zur Check-Prozess-Seite mit ID:', checkInId)
    router.push(`/checks/${checkInId}/process`)
  } else {
    console.error('Keine Check-In-ID in der Antwort gefunden:', checkInResponse.data)
  }
}

// Computed property für gefilterte Terminals
const filteredTerminals = computed(() => {
  // Da wir jetzt direkt die Terminals des Flughafens haben, brauchen wir nicht mehr zu filtern
  return terminals.value
})

// Handler for adding services to existing check-in
const handleAddServices = () => {
  if (activeCheckinData.value?.checkin?.id) {
    // Navigate to add services page with 'add' query parameter
    router.push({
      path: `/checks/${activeCheckinData.value.checkin.id}/services`,
      query: { add: 'true' }
    })
  }
  showActiveCheckinModal.value = false
  activeCheckinData.value = null
}

// Handler for resuming an open check-in
const handleResumeCheckin = async () => {
  const checkinId = activeCheckinData.value?.checkin?.id
  if (!checkinId) return

  showActiveCheckinModal.value = false

  // If taking over from another user, call the resume API to transfer ownership
  if (!isOwnCheckin.value) {
    try {
      await api.post(`/api/checker/checkins/${checkinId}/resume`)
    } catch (err) {
      console.error('Error taking over checkin:', err)
      // Continue anyway - the navigation will work
    }
  }

  // Determine where to navigate based on checkin progress
  const checkin = activeCheckinData.value?.checkin
  const servicesCount = checkin?.services?.total || 0
  const hasMileage = checkin?.mileage != null
  const hasDamages = (checkin?.damages_count || 0) > 0

  if (servicesCount > 0) {
    // Has services, go to services view
    router.push(`/checks/${checkinId}/services`)
  } else if (hasMileage || hasDamages) {
    // Mileage already filled or damages exist, skip to damage page
    router.push(`/checks/${checkinId}/damage`)
  } else {
    // Nothing filled yet, start from process step (mileage/fuel)
    router.push(`/checks/${checkinId}/process`)
  }

  activeCheckinData.value = null
}

// TRK-72: the checker has read the hint — create the check-in now, same
// guarded path as without the hint (a Turnaround later still asks for a reason).
const confirmWorkshopReturn = async () => {
  const payload = pendingCheckinPayload
  showWorkshopReturnModal.value = false
  pendingCheckinPayload = null
  if (!payload) return
  try {
    await submitCheckinGuarded(payload)
  } catch (err: any) {
    console.error('Fehler beim Erstellen des Check-ins:', err)
    alert(err.response?.data?.message || 'Fehler beim Erstellen des Checks. Bitte versuchen Sie es erneut.')
  }
}

const cancelWorkshopReturn = () => {
  showWorkshopReturnModal.value = false
  pendingCheckinPayload = null
  workshopReturn.value = null
}

// TRK-83: off to the Quick Checkout with the plate in the query — the view
// loads the car itself, the backend filters the destinations by role.
const handleQuickCheckout = () => {
  const plate = activeCheckinData.value?.vehicle?.license_plate || vehicleData.value.licensePlate
  showActiveCheckinModal.value = false
  activeCheckinData.value = null
  router.push({ path: '/quick-checkout', query: plate ? { plate } : {} })
}

// Handler for cancelling active check-in modal
const handleCancelActiveCheckin = () => {
  showActiveCheckinModal.value = false
  activeCheckinData.value = null
  // Clear form data to start fresh
  vehicleData.value = {
    licensePlate: '',
    vin: '',
    brand: '',
    model: '',
    rentalCompanyId: '',
    terminalId: vehicleData.value.terminalId, // Keep terminal selection
    fuel_type: '',
    customFieldValues: {}
  }
  vinMode.value = false
  showLicensePlateInput.value = false
  searchCompleted.value = false
  vehicleFound.value = false
  foundVehicleId.value = null
}

const getFieldDisplayValue = (field: CustomField): string => {
  if (!field.field_value) return ''
  
  switch (field.field_type) {
    case 'date':
      try {
        return new Date(field.field_value).toLocaleDateString('de-DE')
      } catch {
        return field.field_value
      }
    case 'number':
      try {
        return parseFloat(field.field_value).toLocaleString('de-DE')
      } catch {
        return field.field_value
      }
    default:
      return field.field_value
  }
}
</script> 